"use client";

import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { trpc } from "@/app/_trpc/client";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Image from "next/image";

export function GallerySection() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0);

  const { data, isLoading, error } = trpc.menu.getMenuItems.useQuery();

  const galleryImages = useMemo(() => {
    if (!data)
      return [] as { id: string; src: string; alt: string; title: string }[];
    const images: { id: string; src: string; alt: string; title: string }[] =
      [];
    for (const item of data) {
      if (item?.image_urls && item.image_urls.length > 0) {
        item.image_urls.forEach((url, idx) => {
          if (!url) return;
          images.push({
            id: `${item.item_id}-${idx}`,
            src: url,
            alt: item.name,
            title: item.name,
          });
        });
      }
    }
    return images;
  }, [data]);

  const VISIBLE_COUNT = 6;
  const visibleImages = galleryImages.slice(
    0,
    Math.min(VISIBLE_COUNT - 1, galleryImages.length)
  );
  const remainingCount = Math.max(
    galleryImages.length - visibleImages.length,
    0
  );

  const openCarousel = (index: number) => {
    setCurrentIndex(index);
    setSelectedImage(index);
  };

  const closeCarousel = () => {
    setSelectedImage(null);
  };

  useEffect(() => {
    if (!carouselApi) return;
    setSelectedCarouselIndex(carouselApi.selectedScrollSnap());
    const onSelect = () =>
      setSelectedCarouselIndex(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect as any);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (selectedImage !== null && carouselApi) {
      carouselApi.scrollTo(currentIndex, true);
    }
  }, [selectedImage, carouselApi, currentIndex]);

  return (
    <>
      <section className="py-20" style={{ backgroundColor: "#f1efe8" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <div className="h-0.5 w-12 bg-secondary mr-4"></div>
              <span className="text-secondary font-medium tracking-wider text-sm">
                GALERIE
              </span>
              <div className="h-0.5 w-12 bg-secondary ml-4"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Selecția noastră culinară
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Descoperiți măiestria din spatele fiecărui fel de mâncare prin
              povestea noastră vizuală a aromelor, ingredientelor și
              experiențelor culinare memorabile.
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleImages.map((image, index) => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-lg cursor-pointer aspect-square"
                onClick={() => openCarousel(index)}
              >
                <Image
                  src={image.src || "/placeholder.svg"}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  width={1000}
                  height={1000}
                />
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <h3 className="text-white text-xl font-semibold text-center px-4">
                      {image.title}
                    </h3>
                  </div>
                </div>
              </div>
            ))}

            {remainingCount > 0 && (
              <div
                key="more"
                className="group relative overflow-hidden rounded-lg cursor-pointer aspect-square"
                onClick={() => openCarousel(visibleImages.length)}
              >
                <Image
                  src={
                    galleryImages[visibleImages.length]?.src ||
                    "/placeholder.svg"
                  }
                  alt="More images"
                  width={1000}
                  height={1000}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
                  <span className="text-white text-3xl font-bold">
                    + {remainingCount}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Carousel Modal */}
      {selectedImage !== null && galleryImages.length > 0 && (
        <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-5xl w-full">
            {/* Close Button */}
            <button
              onClick={closeCarousel}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-800/50 hover:bg-slate-800 transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>

            <Carousel
              className="w-full"
              opts={{ startIndex: currentIndex }}
              setApi={setCarouselApi}
            >
              <CarouselContent>
                {galleryImages.map((image) => (
                  <CarouselItem key={image.id}>
                    <div className="flex items-center justify-center bg-slate-800 rounded-lg">
                      <Image
                        src={image.src || "/placeholder.svg"}
                        alt={image.alt}
                        width={1000}
                        height={1000}
                        className="w-auto h-auto max-w-[90vw] max-h-[80vh]"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="-left-4" />
              <CarouselNext className="-right-4" />
            </Carousel>

            {/* Image Info */}
            <div className="mt-4 text-center">
              <h3 className="text-white text-2xl font-semibold mb-2">
                {galleryImages[selectedCarouselIndex]?.title}
              </h3>
              <p className="text-gray-300">
                {selectedCarouselIndex + 1} din {galleryImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
